import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Toast from '../components/Toast';
import ColisCard from '../components/ColisCard';
import ModalPrompt from '../components/ModalPrompt';
import { useApp } from '../data/ColisContext';

function ActionBtn({ children, onClick, disabled, color, bg }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '10px 14px',
        borderRadius: 12,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 900,
        fontSize: 13,
        fontFamily: 'inherit',
        background: disabled ? '#E2E8F0' : (bg || 'linear-gradient(135deg, #4F46E5, #8B5CF6)'),
        color: disabled ? '#64748b' : (color || 'white'),
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function isInternationalStatut(statut) {
  return ['entrepot_france', 'transit', 'entrepot_senegal', 'dispatching', 'affecte', 'en_route', 'livre'].includes(statut);
}

function isValiseColis(c) {
  const id = String(c?.id || '').toUpperCase();
  if (id.startsWith('VAL-')) return true;
  const type = String(c?.meta?.type || '').toLowerCase();
  return type === 'valise';
}

export default function AgentPage() {
  const navigate = useNavigate();
  const { user, colis, updateStatut, addProblem, showToast, createValise } = useApp();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('actifs');
  const [valiseModalOpen, setValiseModalOpen] = useState(false);
  const [problemModal, setProblemModal] = useState({ open: false, colisId: '' });

  const normalizePhone = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    let s = raw.replace(/[^0-9]/g, '');
    if (s.startsWith('00')) s = s.slice(2);
    return s;
  };

  const safeUpdate = async (id, statut, extra) => {
    try {
      if (typeof updateStatut !== 'function') {
        showToast('Fonction de mise à jour indisponible (frontend à redéployer)', 'error');
        return;
      }
      await updateStatut(id, statut, extra);
      showToast('Statut mis à jour', 'success');
    } catch (e) {
      showToast(e?.message || 'Erreur', 'error');
    }
  };

  const list = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    return (Array.isArray(colis) ? colis : [])
      .filter(c => {
        const valiseId = c?.meta?.valise_id;
        return !(valiseId && String(valiseId).trim());
      })
      .filter(c => {
        if (filter === 'clients') return false;
        if (filter === 'actifs') return c.statut !== 'livre';
        if (filter === 'livres') return c.statut === 'livre';
        if (filter === 'problemes') {
          const problems = c?.meta?.problems;
          return Array.isArray(problems) && problems.length > 0;
        }
        return true;
      })
      .filter(c => {
        if (!q) return true;
        const hay = [c?.id, c?.destinataire, c?.tel, c?.nom].map(x => String(x || '').toLowerCase()).join(' ');
        return hay.includes(q);
      })
      .sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')));
  }, [colis, filter, query]);

  const clients = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const map = new Map();
    for (const c of (Array.isArray(colis) ? colis : [])) {
      const telNorm = normalizePhone(c?.tel);
      const tel = String(c?.tel || '').trim();
      const destinataire = String(c?.destinataire || '').trim();
      if (!telNorm || !destinataire) continue;
      const key = telNorm;
      const prev = map.get(key);
      const date = String(c?.date || '');
      const next = {
        tel,
        telNorm,
        destinataire,
        lastDate: date,
        count: (prev?.count || 0) + 1,
      };
      if (!prev || String(date || '').localeCompare(String(prev?.lastDate || '')) > 0) {
        map.set(key, next);
      } else {
        map.set(key, { ...prev, count: next.count });
      }
    }
    return Array.from(map.values())
      .filter(x => {
        if (!q) return true;
        const hay = `${x?.destinataire || ''} ${x?.tel || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => String(b?.lastDate || '').localeCompare(String(a?.lastDate || '')));
  }, [colis, query]);

  const workflowStep = (statut) => {
    if (statut === 'depot') return { label: '🇫🇷 Dépôt France', next: 'transit' };
    if (statut === 'entrepot_france') return { label: '🇫🇷 Arrivé entrepôt (Paris)', next: 'transit' };
    if (statut === 'transit') return { label: '✈️ Transit', next: 'entrepot_senegal' };
    if (statut === 'entrepot_senegal') return { label: '🇸🇳 Arrivé entrepôt (Sénégal)', next: 'dispatching' };
    if (statut === 'dispatching') return { label: '🧩 Dispatching', next: 'affecte' };
    if (statut === 'affecte') return { label: '✅ Affecté à un livreur', next: 'en_route' };
    if (statut === 'en_route') return { label: '🚚 En route', next: 'livre' };
    if (statut === 'livre') return { label: '🎉 Livré', next: null };
    return null;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 20px' }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#4F46E5', fontWeight: 600, fontSize: 14,
          marginBottom: 22, display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'inherit', padding: 0,
        }}>
          ← Retour
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>Espace Agent</div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 700, marginTop: 4 }}>
              {user?.email || '—'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/depot')}
              style={{
                padding: '12px 14px',
                background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontWeight: 900,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(79,70,229,0.25)',
              }}
            >
              ➕ Créer un colis
            </button>

            <button
              type="button"
              onClick={() => setValiseModalOpen(true)}
              style={{
                padding: '12px 14px',
                background: 'linear-gradient(135deg, #0EA5E9, #4F46E5)',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontWeight: 900,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(14,165,233,0.25)',
              }}
            >
              🧳 Créer une valise
            </button>

            <button
              type="button"
              onClick={() => navigate('/mes-valises')}
              style={{
                padding: '12px 14px',
                background: '#F1F5F9',
                color: '#0f172a',
                border: 'none',
                borderRadius: 12,
                fontWeight: 900,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              🧳 Mes valises
            </button>
          </div>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: 16,
          padding: 12,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'actifs', label: 'Actifs' },
              { key: 'livres', label: 'Livrés' },
              { key: 'problemes', label: 'Problèmes' },
              { key: 'clients', label: 'Clients' },
              { key: 'tous', label: 'Tous' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 12,
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 900,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  background: filter === t.key ? 'linear-gradient(135deg, #4F46E5, #8B5CF6)' : '#F1F5F9',
                  color: filter === t.key ? 'white' : '#334155',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Rechercher (code, tel, destinataire...)"
            style={{
              padding: '10px 14px',
              borderRadius: 12,
              border: '2px solid #e2e8f0',
              fontSize: 13,
              fontFamily: 'inherit',
              minWidth: 260,
              outline: 'none',
            }}
          />
        </div>

        <div style={{
          background: '#EEF2FF',
          border: '1px solid #C7D2FE',
          borderRadius: 16,
          padding: 12,
          color: '#1e1b4b',
          fontSize: 13,
          fontWeight: 800,
          marginBottom: 16,
        }}>
          Workflow international: France → Transit → Sénégal → Dispatching → Affecté → En route → Livré
        </div>

        {filter === 'clients' ? (
          <div style={{
            background: 'white',
            borderRadius: 18,
            padding: 18,
            border: '1px solid #e2e8f0',
            color: '#0f172a',
            fontWeight: 800,
          }}>
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Clients / Destinataires enregistrés</div>
            {clients.length === 0 ? (
              <div style={{ color: '#475569', fontWeight: 800 }}>Aucun client trouvé.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                {clients.map((c, idx) => (
                  <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: 12, background: '#f8fafc' }}>
                    <div style={{ fontWeight: 900, color: '#0f172a', fontSize: 14 }}>{c.destinataire}</div>
                    <div style={{ fontWeight: 800, color: '#475569', fontSize: 13, marginTop: 4 }}>📞 {c.tel}</div>
                    <div style={{ fontWeight: 800, color: '#64748b', fontSize: 12, marginTop: 6 }}>Colis: {c.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : list.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: 18,
            padding: 18,
            border: '1px solid #e2e8f0',
            color: '#475569',
            fontWeight: 800,
          }}>
            Aucun colis trouvé.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
            {list.map(c => (
              <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <ColisCard colis={c} readOnly />

                <div style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 16,
                  padding: 12,
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 900 }}>
                    Actions international
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {(() => {
                      if (isValiseColis(c) && c?.statut === 'dispatching') return null;
                      const step = workflowStep(c?.statut);
                      if (!step || !step.next) return null;
                      return (
                        <ActionBtn onClick={() => safeUpdate(c.id, step.next)}>
                          {step.label}
                        </ActionBtn>
                      );
                    })()}

                    <ActionBtn
                      color="#92400E"
                      bg="#FFEDD5"
                      onClick={async () => {
                        setProblemModal({ open: true, colisId: c.id });
                      }}
                    >
                      ⚠️ Problème
                    </ActionBtn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <ModalPrompt
        open={valiseModalOpen}
        title="🧳 Créer une valise"
        description="Saisissez un nom pour la valise."
        placeholder="Ex: Valise Dakar"
        confirmLabel="Créer"
        onClose={() => setValiseModalOpen(false)}
        onConfirm={async (nom) => {
          const n = String(nom || '').trim();
          if (!n) {
            showToast('Nom requis', 'error');
            return;
          }
          try {
            await createValise(n);
            setValiseModalOpen(false);
          } catch (e) {
            showToast(e?.message || 'Erreur', 'error');
          }
        }}
      />

      <ModalPrompt
        open={problemModal.open}
        title="⚠️ Signaler un problème"
        description="Décrivez le problème rencontré."
        placeholder="Ex: Adresse incomplète, numéro injoignable..."
        confirmLabel="Enregistrer"
        cancelLabel="Annuler"
        multiline
        onClose={() => setProblemModal({ open: false, colisId: '' })}
        onConfirm={async (msg) => {
          const id = problemModal.colisId;
          const m = String(msg || '').trim();
          if (!id) return;
          if (!m) {
            showToast('Message requis', 'error');
            return;
          }
          try {
            if (typeof addProblem !== 'function') {
              showToast('Action indisponible (frontend à redéployer)', 'error');
              return;
            }
            await addProblem(id, m);
            setProblemModal({ open: false, colisId: '' });
          } catch (e) {
            showToast(e?.message || 'Erreur', 'error');
          }
        }}
      />

      <Toast />
    </div>
  );
}
